import {
	ConnectionProblemException,
	ServerErrorResponseException,
	UnableToParseServerResponseException
} from '@inter-ikea-kompis/exceptions';

type Options = {
	body?: string;
	method?: string;
	headers?: HeadersInit;
	credentials?: RequestCredentials;
};

/**
 * Service fetch utility.
 */
export default class ServiceFetch {
	/**
	 * Fetches JSON and handles error responses.
	 *
	 * @param requestInfo Request info.
	 * @param [options] Fetch options.
	 * @param [options.headers] Fetch options.
	 * @param [options.credentials] Fetch options.
	 * @param [options.statusHandlers] Object with status handlers for a specific status code. Example: { 419: async () => { return this.__getUser() } }.
	 * @returns Promise.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public static async json(requestInfo: string, options?: Options): Promise<any> {
		const response = await this.fetch(requestInfo, options);

		if (typeof response.json === 'function') {
			try {
				return await response.json();
			} catch (error) {
				throw new UnableToParseServerResponseException(response, error.message);
			}
		}

		return response;
	}

	/**
	 * Fetches text and handles error responses.
	 *
	 * @param requestInfo Request info.
	 * @param [options] Fetch options.
	 * @param [options.headers] Fetch options.
	 * @param [options.credentials] Fetch options.
	 * @param [options.statusHandlers] Object with status handlers for a specific status code. Example: { 419: async () => { return this.__getUser() } }.
	 * @returns Promise.
	 */
	public static async text(requestInfo: RequestInfo, options?: Options): Promise<string | null> {
		const response = await this.fetch(requestInfo, options);

		if (typeof response.text === 'function') {
			try {
				return await response.text();
			} catch (error) {
				throw new UnableToParseServerResponseException(response, error.message);
			}
		}

		return null;
	}

	/**
	 * Fetches JSON and handles error responses.
	 *
	 * @param requestInfo Request info.
	 * @param [options] Fetch options.
	 * @param [options.headers] Fetch options.
	 * @param [options.credentials] Fetch options.
	 * @param [options.statusHandlers] Object with status handlers for a specific status code. Example: { 419: async () => { return this.__getUser() } }.
	 * @returns Promise.
	 */
	private static async fetch(requestInfo: RequestInfo, options?: Options): Promise<Response> {
		let response;

		try {
			response = await window.fetch(requestInfo, options);
		} catch (error) {
			throw new ConnectionProblemException(requestInfo, error.message);
		}

		if (!response.ok) {
			throw new ServerErrorResponseException(response);
		}

		return response;
	}
}
